/**
 * Teacher: student CRUD page.
 * GET /api/students, POST /api/students { name, pin }, DELETE /api/students/:id
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';

interface StudentRow {
  id: string;
  name: string;
  created_at: string;
}

export function StudentList() {
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [name, setName] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const data = await api.get<{ students: StudentRow[] }>('/api/students');
      setStudents(data.students);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/students', { name: name.trim(), pin });
      setName('');
      setPin('');
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('정말 삭제할까요?')) return;
    try {
      await api.delete(`/api/students/${id}`);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <main className="page-narrow">
      <Link to="/teacher" className="back-link">대시보드</Link>
      <h1 className="mb-6">Student Management</h1>

      <div className="form-section">
        <form onSubmit={handleCreate} className="form-row" data-testid="create-student-form" aria-label="학생 추가">
          <div className="flex-1">
            <label htmlFor="new-student-name" className="sr-only">학생 이름</label>
            <input
              id="new-student-name"
              data-testid="new-student-name"
              className="input"
              placeholder="이름"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="flex-1">
            <label htmlFor="new-student-pin" className="sr-only">학생 PIN</label>
            <input
              id="new-student-pin"
              data-testid="new-student-pin"
              className="input"
              placeholder="PIN"
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              required
            />
          </div>
          <button
            data-testid="create-student-submit"
            type="submit"
            disabled={busy}
            className="btn btn-gold shrink-0"
            aria-busy={busy}
          >
            {busy ? '추가 중…' : '추가'}
          </button>
        </form>
      </div>

      {error && (
        <p data-testid="student-error" className="error-text mb-4" role="alert">
          {error}
        </p>
      )}

      {students.length === 0 ? (
        <div className="empty-state">
          <p>등록된 학생이 없습니다</p>
          <p className="list-item-meta mt-2">위 양식으로 첫 학생을 추가해 보세요</p>
        </div>
      ) : (
      <div className="table-wrap">
        <table aria-label="학생 목록">
          <thead>
            <tr>
              <th>Name</th>
              <th>Enrolled</th>
              <th className="col-action"><span className="sr-only">작업</span></th>
            </tr>
          </thead>
          <tbody data-testid="student-list">
            {students.map((s) => (
              <tr key={s.id} data-testid={`student-row-${s.id}`}>
                <td className="font-medium">{s.name}</td>
                <td>{s.created_at?.slice(0, 10) ?? ''}</td>
                <td>
                  <button
                    data-testid={`delete-${s.id}`}
                    onClick={() => handleDelete(s.id)}
                    className="btn btn-danger"
                    aria-label={`${s.name} 삭제`}
                  >
                    삭제
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </main>
  );
}
